import isURL from 'validator/es/lib/isURL';
import { cloneDeepWith } from 'lodash';
import { clientError, translatedError, alphaNumUnderscore } from '@/utils/errors';
import { hasMinimumLength, replaceEmptyString } from '@/utils/resultsHelper';
import fields, { globalFieldLengths } from '@/constants/fields';
import { isSplitUrl, joinUrl } from '@/constants/urls';

const lookupKeys = (dictionary, oldName) => {
  const keys = Object.keys(dictionary.lookups || {});
  const index = keys.indexOf(oldName);

  if (index > -1) {
    keys.splice(index, 1);
  }
  return keys.map((key) => key.toLowerCase());
};

export const validateLookup = (dictionary, errors, intl, oldName, newName) => {
  const keys = lookupKeys(dictionary, oldName);
  const context = { entity: 'dictionary', dictionaryUUID: dictionary.uuid };

  if (!hasMinimumLength(newName)) {
    errors.push(clientError(
      context,
      null,
      intl.formatMessage({ id: 'validation.name.required' }),
      [fields.NAME],
    ));
  }

  if (newName && !alphaNumUnderscore(newName)) {
    errors.push(clientError(
      context,
      null,
      intl.formatMessage({ id: 'validation.name.alphanumeric' }),
      [fields.NAME],
    ));
  }

  if (newName && newName.length > globalFieldLengths.name) {
    errors.push(clientError(
      { ...context, lookupName: newName },
      null,
      intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.name }),
      [fields.NAME],
    ));
  }

  if (newName && keys.includes(newName.toLowerCase())) {
    errors.push(clientError(
      { ...context, lookupName: newName },
      null,
      intl.formatMessage({ id: 'validation.name.not.unique' }),
      [fields.NAME],
    ));
  }
};

const validateLookupItemOptions = (dictionary, errors, intl, lookupItem, name) => {
  const context = {
    entity: 'dictionary', dictionaryUUID: dictionary.uuid, lookupName: name, lookupItemUUID: lookupItem.uuid,
  };

  if (!hasMinimumLength(lookupItem.name)) {
    errors.push(clientError(
      context,
      null,
      intl.formatMessage({ id: 'validation.name.required' }),
      [fields.NAME],
    ));
  }

  if (lookupItem.name && lookupItem.name.length > globalFieldLengths.name) {
    errors.push(clientError(
      context,
      null,
      intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.name }),
      [fields.NAME],
    ));
  }

  if (lookupItem.description && lookupItem.description.length > globalFieldLengths.description) {
    errors.push(clientError(
      context,
      null,
      intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.description }),
      [fields.DESCRIPTION],
    ));
  }

  if (lookupItem.uri && lookupItem.uri.length > globalFieldLengths.url) {
    errors.push(clientError(
      context,
      null,
      intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.url }),
      [fields.URI],
    ));
  }

  if (lookupItem.uri && !isURL(lookupItem.uri)) {
    errors.push(clientError(
      context,
      null,
      intl.formatMessage({ id: 'validation.uri.not.valid' }),
      [fields.URI],
    ));
  }
};

const validateLookupItem = (dictionary, errors, intl, items, name) => {
  if (items.length === 0) {
    errors.push(clientError(
      {
        entity: 'dictionary', dictionaryUUID: dictionary.uuid, lookupName: name,
      },
      null,
      intl.formatMessage({ id: 'save.dataset.validation.required.lookup.items' }),
      [fields.VALUE],
    ));
  }

  items.forEach((lookupItem) => {
    validateLookupItemOptions(dictionary, errors, intl, lookupItem, name);
  });
};

const validateLookupItems = (dictionary, errors, intl) => {
  Object.keys(dictionary.lookups || {}).forEach((name) => {
    const items = dictionary.lookups[name].options;

    validateLookupItem(dictionary, errors, intl, items, name);
  });
};

const valueTransform = (value) => (isSplitUrl(value) ? joinUrl(value) : replaceEmptyString(value));

export const transformLookup = (lookup) => cloneDeepWith(lookup, valueTransform);

export const transformLookups = (lookups) => Object.keys(lookups).reduce(
  (attrs, key) => ({
    ...attrs,
    [key]: transformLookup(lookups[key]),
  }),
  {},
);

const validationWarningDictionaryLookup = (dictionaryUUID, lookupName, warnings) => {
  const dictionaryLookupWarning = warnings.find((o) => o.context.lookupName === lookupName);
  if (dictionaryLookupWarning) {
    const context = {
      entity: 'dictionary', dictionaryUUID, lookupName,
    };
    const warning = translatedError(
      context,
      null,
      'validation.lookup.items.duplicated',
      [fields.NAME],
    );
    warning.warning = true;
    warnings.push(warning);
  }
};

export const validationWarningDictionaryLookupOptions = (dictionaryUUID, options, lookupName, warnings) => {
  const optionNames = options.map((o) => o.name.toLowerCase()).filter((o) => o);
  options.forEach((o) => {
    const context = {
      entity: 'dictionary', dictionaryUUID, lookupName, lookupItemUUID: o.uuid,
    };
    if (optionNames.filter((n) => n === o.name.toLowerCase()).length > 1) {
      const warning = translatedError(
        context,
        null,
        'validation.name.not.unique',
        [fields.NAME],
      );
      warning.warning = true;
      warnings.push(warning);
    }
  });
  validationWarningDictionaryLookup(dictionaryUUID, lookupName, warnings);
};

export const validationWarningDictionaryLookups = (lookups, dictionaryUUID, warnings) => {
  Object.entries(lookups).forEach(([name, optionsObj]) => {
    const { options } = optionsObj;
    validationWarningDictionaryLookupOptions(dictionaryUUID, options, name, warnings);
  });
};

export const validationWarningDictionariesLookups = (dictionaries, warnings) => {
  dictionaries.forEach((dictionary) => {
    const { lookups, uuid } = dictionary;
    validationWarningDictionaryLookups(lookups, uuid, warnings);
  });
};

export default validateLookupItems;
